import { create } from 'zustand';
import type {
  DiscoveryRequestSummary, DiscoveryRequestDetail,
  DiscoveryMessage, Stakeholder, AgentAction, DiscoveryView,
} from '../features/discovery/types';
import { discoveryApi } from '../services/api/discovery';

const _discoveryInflight: Record<string, Promise<void>> = {};

interface DiscoveryState {
  view: DiscoveryView;
  requests: DiscoveryRequestSummary[];
  activeRequest: DiscoveryRequestDetail | null;
  activeRequestId: string | null;
  unreadCount: number;
  isLoading: boolean;
  isStreaming: boolean;
  streamingText: string;
  pendingActions: AgentAction[];
  error: string | null;
}

interface DiscoveryActions {
  setView: (view: DiscoveryView) => void;
  loadRequests: (status?: string) => Promise<void>;
  loadRequest: (id: string) => Promise<void>;
  setActiveRequestId: (id: string | null) => void;
  createRequest: (description: string) => Promise<DiscoveryRequestSummary>;
  loadUnreadCount: () => Promise<void>;
  setStreaming: (streaming: boolean) => void;
  appendStreamToken: (token: string) => void;
  setStreamingText: (text: string) => void;
  setPendingActions: (actions: AgentAction[]) => void;
  addMessage: (message: DiscoveryMessage) => void;
  addStakeholder: (stakeholder: Stakeholder) => void;
  updateInitiatorConversationStatus: (status: 'active' | 'paused') => void;
  updateRequestStatus: (id: string, status: string) => Promise<void>;
  deleteRequest: (id: string) => Promise<void>;
  setError: (error: string | null) => void;
  reset: () => void;
}

const INITIAL_STATE: DiscoveryState = {
  view: 'board',
  requests: [],
  activeRequest: null,
  activeRequestId: null,
  unreadCount: 0,
  isLoading: false,
  isStreaming: false,
  streamingText: '',
  pendingActions: [],
  error: null,
};

export const useDiscoveryStore = create<DiscoveryState & DiscoveryActions>((set, get) => ({
  ...INITIAL_STATE,

  setView: (view) => set({ view }),

  loadRequests: (status) => {
    const key = `requests:${status ?? 'all'}`;
    if (_discoveryInflight[key]) return _discoveryInflight[key];
    _discoveryInflight[key] = (async () => {
      set({ isLoading: true, error: null });
      try {
        const { data } = await discoveryApi.listRequests(status);
        set({ requests: data, isLoading: false });
      } catch (e: any) {
        set({ error: e.message, isLoading: false });
      } finally {
        delete _discoveryInflight[key];
      }
    })();
    return _discoveryInflight[key];
  },

  loadRequest: (id) => {
    const key = `request:${id}`;
    if (_discoveryInflight[key]) return _discoveryInflight[key];
    _discoveryInflight[key] = (async () => {
      set({ isLoading: true, error: null, activeRequestId: id });
      try {
        const { data } = await discoveryApi.getRequest(id);
        set((s) => ({ activeRequest: data, isLoading: false, view: s.view === 'board' ? 'chat' : s.view }));
      } catch (e: any) {
        set({ error: e.message, isLoading: false });
      } finally {
        delete _discoveryInflight[key];
      }
    })();
    return _discoveryInflight[key];
  },

  setActiveRequestId: (id) => set({ activeRequestId: id }),

  createRequest: async (description) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await discoveryApi.createRequest(description);
      set((s) => ({
        requests: [data, ...s.requests],
        isLoading: false,
      }));
      return data;
    } catch (e: any) {
      set({ error: e.message, isLoading: false });
      throw e;
    }
  },

  loadUnreadCount: () => {
    if (_discoveryInflight['unread']) return _discoveryInflight['unread'];
    _discoveryInflight['unread'] = (async () => {
      try {
        const { data } = await discoveryApi.getUnreadCount();
        set({ unreadCount: data.count });
      } catch {
        // silent fail
      } finally {
        delete _discoveryInflight['unread'];
      }
    })();
    return _discoveryInflight['unread'];
  },

  setStreaming: (streaming) => set({ isStreaming: streaming, ...(streaming ? { streamingText: '' } : {}) }),
  appendStreamToken: (token) => set((s) => ({ streamingText: s.streamingText + token })),
  setStreamingText: (text) => set({ streamingText: text }),
  setPendingActions: (actions) => set({ pendingActions: actions }),

  addMessage: (message) =>
    set((s) => ({
      activeRequest: s.activeRequest
        ? { ...s.activeRequest, messages: [...s.activeRequest.messages, message] }
        : null,
    })),

  addStakeholder: (stakeholder) =>
    set((s) => ({
      activeRequest: s.activeRequest
        ? { ...s.activeRequest, stakeholders: [...s.activeRequest.stakeholders, stakeholder] }
        : null,
    })),

  updateInitiatorConversationStatus: (status) =>
    set((s) => ({
      activeRequest: s.activeRequest ? {
        ...s.activeRequest,
        stakeholders: s.activeRequest.stakeholders.map((sh) =>
          sh.role === 'initiator' ? { ...sh, conversation_status: status } : sh
        ),
      } : null,
    })),

  updateRequestStatus: async (id, status) => {
    try {
      await discoveryApi.updateStatus(id, status);
      set((s) => ({
        requests: s.requests.map((r) => (r.id === id ? { ...r, status: status as any } : r)),
        activeRequest: s.activeRequest?.id === id ? { ...s.activeRequest, status: status as any } : s.activeRequest,
      }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  deleteRequest: async (id) => {
    try {
      await discoveryApi.deleteRequest(id);
      set((s) => ({
        requests: s.requests.filter((r) => r.id !== id),
        activeRequest: s.activeRequest?.id === id ? null : s.activeRequest,
        activeRequestId: s.activeRequestId === id ? null : s.activeRequestId,
        view: s.activeRequestId === id ? 'board' : s.view,
      }));
    } catch (e: any) {
      set({ error: e.message });
      throw e;
    }
  },

  setError: (error) => set({ error }),
  reset: () => set(INITIAL_STATE),
}));
